/**
 * Unified Review & Sign modal: draft checks, AI checks, and scrubber accept/decline.
 * Continue opens the signature capture step when all actionable items are resolved.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { IconCheck, IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { applyAcceptedScrubberResult } from '@/lib/encounterScrubber/applyScrubberDecisions';
import {
  scrubberResultRowKey,
  type ScrubberSuggestionDecision,
} from '@/lib/encounterScrubber/scrubberReview';
import type { EncounterScrubberResult } from '@/lib/encounterScrubber/encounterScrubber';
import type { MetadataValidationError } from '@/lib/encounterScrubber/encounterScrubber';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import type { BillingServiceTypeRef } from '../noteMetadataMapping';
import type { AiNoteCheckResult, NoteCheckFinding } from '../fhirNoteMapping';
import { getCheckDisplayLabel } from '../noteCheckConstants';

export type NoteReviewContinueAction = 'sign' | 'finish';

export interface NoteReviewAndSignModalProps {
  opened: boolean;
  onCancel: () => void;
  /** Apply CPT decisions and proceed to sign/finish. */
  onContinue: (decisions: Record<string, ScrubberSuggestionDecision>) => void;
  continueAction?: NoteReviewContinueAction;
  loading?: boolean;
  /** True while draft/AI/scrubber checks are still being collected. */
  checksLoading?: boolean;
  checksError?: string | null;
  draftChecks?: AiNoteCheckResult[];
  automaticChecks?: AiNoteCheckResult[];
  scrubberResults?: EncounterScrubberResult[];
  metadataErrors?: MetadataValidationError[];
  /** When true, Continue is blocked (e.g. missing billing metadata). */
  blocked?: boolean;
  blockedMessage?: string;
  /** Current note metadata; used to apply accepted recommendations immediately. */
  noteMetadata?: NoteMetadataSnapshot | null;
  serviceTypes?: BillingServiceTypeRef[];
  /** Called when a single recommendation is accepted and billing codes are updated. */
  onRecommendationApplied?: (metadata: NoteMetadataSnapshot) => void;
  /** Close the modal and jump to the question behind a flagged check finding. */
  onNavigateToFinding?: (itemPath: string) => void;
}

function severityColor(severity: EncounterScrubberResult['severity']): string {
  switch (severity) {
    case 'error':
      return 'red';
    case 'warning':
      return 'yellow';
    case 'info':
    default:
      return 'blue';
  }
}

function actionLabel(action: EncounterScrubberResult['action']): string {
  switch (action) {
    case 'keep':
      return 'Keep';
    case 'suggest_replace':
      return 'Suggest replace';
    case 'suggest_add':
      return 'Suggest add';
    case 'suggest_remove':
      return 'Suggest remove';
    case 'flag_unsupported':
      return 'Unsupported';
    case 'do_not_add':
      return 'Do not add';
    case 'billing_review':
      return 'Billing review';
    default:
      return action;
  }
}

function suggestionText(result: EncounterScrubberResult): string | undefined {
  if (result.suggestedCode) return `→ ${result.suggestedCode}`;
  if (result.suggestedCodes?.length) return `→ ${result.suggestedCodes.join(', ')}`;
  if (result.units != null) return `Units: ${result.units}`;
  return undefined;
}

function acceptHint(result: EncounterScrubberResult): string | undefined {
  switch (result.action) {
    case 'suggest_replace':
      return result.suggestedCode
        ? `Accept updates CPT to ${result.suggestedCode}.`
        : undefined;
    case 'suggest_add':
      return result.suggestedCode
        ? `Accept adds CPT ${result.suggestedCode}${result.units != null ? ` (${result.units} unit${result.units === 1 ? '' : 's'})` : ''}.`
        : undefined;
    case 'suggest_remove':
    case 'flag_unsupported':
      return `Accept removes CPT ${result.code}.`;
    default:
      return undefined;
  }
}

function isPassing(status: AiNoteCheckResult['status']): boolean {
  return status === 'passed';
}

const NAVIGABLE_FINDING_LIMIT = 8;

/** One row per question, so repeated findings on the same field collapse. */
function navigableFindings(check: AiNoteCheckResult): NoteCheckFinding[] {
  const seen = new Set<string>();
  const out: NoteCheckFinding[] = [];
  for (const finding of check.findings ?? []) {
    if (!finding.itemPath || seen.has(finding.itemPath)) continue;
    seen.add(finding.itemPath);
    out.push(finding);
  }
  return out;
}

function CheckRow({
  check,
  onNavigateToFinding,
}: {
  check: AiNoteCheckResult;
  onNavigateToFinding?: (itemPath: string) => void;
}): JSX.Element {
  const passed = isPassing(check.status);
  const findings = !passed && onNavigateToFinding ? navigableFindings(check) : [];
  const shown = findings.slice(0, NAVIGABLE_FINDING_LIMIT);
  const hiddenCount = findings.length - shown.length;

  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <ThemeIcon
        size={22}
        radius="xl"
        variant="light"
        color={passed ? 'gray' : 'red'}
        style={{ flexShrink: 0 }}
      >
        {passed ? <IconCheck size={14} /> : <IconInfoCircle size={14} />}
      </ThemeIcon>
      <Stack gap={2} style={{ flex: 1 }}>
        <Text size="sm" c={passed ? 'dark' : 'red'} style={{ lineHeight: 1.4 }}>
          {getCheckDisplayLabel(check.id, check.status, check.label)}
        </Text>
        {shown.length > 0 ? (
          <Stack gap={2}>
            {shown.map((finding) => (
              <UnstyledButton
                key={finding.itemPath}
                onClick={() => onNavigateToFinding?.(finding.itemPath as string)}
                style={{ textAlign: 'left' }}
              >
                <Text size="xs" c="red" td="underline">
                  {finding.location}
                </Text>
              </UnstyledButton>
            ))}
            {hiddenCount > 0 ? (
              <Text size="xs" c="dimmed">
                +{hiddenCount} more
              </Text>
            ) : null}
          </Stack>
        ) : !passed && check.detail ? (
          <Text size="xs" c="dimmed">
            {check.detail}
          </Text>
        ) : null}
      </Stack>
    </Group>
  );
}

export function NoteReviewAndSignModal({
  opened,
  onCancel,
  onContinue,
  continueAction = 'sign',
  loading = false,
  checksLoading = false,
  checksError = null,
  draftChecks = [],
  automaticChecks = [],
  scrubberResults = [],
  metadataErrors = [],
  blocked = false,
  blockedMessage,
  noteMetadata = null,
  serviceTypes = [],
  onRecommendationApplied,
  onNavigateToFinding,
}: NoteReviewAndSignModalProps): JSX.Element {
  const rowKeys = useMemo(
    () => scrubberResults.map((result, index) => scrubberResultRowKey(result, index)),
    [scrubberResults],
  );

  const [decisions, setDecisions] = useState<Record<string, ScrubberSuggestionDecision>>({});
  const appliedMetadataRef = useRef<NoteMetadataSnapshot | null>(null);

  useEffect(() => {
    if (!opened) return;
    setDecisions({});
    appliedMetadataRef.current = noteMetadata;
  }, [opened, rowKeys.join('|')]);

  const allCptDecided =
    scrubberResults.length === 0 || rowKeys.every((key) => decisions[key] != null);

  /** Required questions are contractual, unlike the advisory quality checks. */
  const missingRequiredFields = useMemo(
    () => draftChecks.some((c) => c.id === 'required-fields' && c.status === 'flagged'),
    [draftChecks],
  );

  const hasFlaggedQuality = useMemo(() => {
    const all = [...draftChecks, ...automaticChecks];
    return all.some(
      (c) =>
        c.id !== 'required-fields' &&
        (c.status === 'flagged' || c.status === 'review_recommended'),
    );
  }, [draftChecks, automaticChecks]);

  const blockingMessage = blocked
    ? (blockedMessage ?? 'Resolve blocking billing or required-field issues before signing.')
    : missingRequiredFields
      ? 'Complete the required questions listed below before signing. Select one to jump to it.'
      : undefined;

  const canContinue =
    !checksLoading &&
    !blockingMessage &&
    allCptDecided &&
    !loading &&
    metadataErrors.every((e) => !e.blocksSave);

  const continueLabel = continueAction === 'finish' ? 'Continue' : 'Continue → Sign';

  const handleAccept = (key: string, result: EncounterScrubberResult) => {
    if (decisions[key] === 'accept') return;
    setDecisions((prev) => ({ ...prev, [key]: 'accept' }));
    const base = appliedMetadataRef.current ?? noteMetadata;
    if (!base) return;
    const next = applyAcceptedScrubberResult(base, result, serviceTypes);
    appliedMetadataRef.current = next;
    onRecommendationApplied?.(next);
  };

  const handleReject = (key: string) => {
    setDecisions((prev) => ({ ...prev, [key]: 'reject' }));
  };

  const handleContinue = () => {
    if (!canContinue) return;
    onContinue(decisions);
  };

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title="Review & Sign"
      size="lg"
      centered
      closeOnClickOutside={!loading && !checksLoading}
      closeOnEscape={!loading && !checksLoading}
    >
      <Stack gap="md">
        <Text size="sm" c="gray.7">
          Review quality checks and CPT suggestions before signing. Accept or reject each billing
          suggestion, then continue to sign.
        </Text>

        {checksLoading ? (
          <Group gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Running quality and billing checks…
            </Text>
          </Group>
        ) : null}

        {checksError ? (
          <Alert color="yellow" title="Automatic review incomplete" variant="light">
            <Text size="sm">{checksError}</Text>
            <Text size="xs" c="dimmed" mt={4}>
              You can still continue after reviewing the note manually.
            </Text>
          </Alert>
        ) : null}

        {!checksLoading && blockingMessage ? (
          <Alert color="red" title="Cannot continue" variant="light">
            <Text size="sm">{blockingMessage}</Text>
          </Alert>
        ) : null}

        {metadataErrors.length > 0 ? (
          <Alert color="yellow" title="Billing metadata gaps" variant="light">
            <Stack gap={4}>
              {metadataErrors.map((err) => (
                <Text key={err.field} size="sm">
                  {err.message}
                </Text>
              ))}
            </Stack>
          </Alert>
        ) : null}

        {!checksLoading && draftChecks.length > 0 ? (
          <Stack gap="xs">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Draft checks
            </Text>
            {draftChecks.map((check) => (
              <CheckRow
                key={`draft-${check.id}`}
                check={check}
                onNavigateToFinding={onNavigateToFinding}
              />
            ))}
          </Stack>
        ) : null}

        {!checksLoading && automaticChecks.length > 0 ? (
          <Stack gap="xs">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Automatic checks
            </Text>
            {automaticChecks.map((check) => (
              <CheckRow
                key={`auto-${check.id}`}
                check={check}
                onNavigateToFinding={onNavigateToFinding}
              />
            ))}
          </Stack>
        ) : null}

        {!checksLoading && hasFlaggedQuality && !blockingMessage ? (
          <Text size="xs" c="dimmed">
            Flagged quality checks are warnings. Review them before signing; they do not block
            Continue.
          </Text>
        ) : null}

        {!checksLoading && scrubberResults.length > 0 ? (
          <Stack gap="xs">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Recommendations
            </Text>
            {scrubberResults.map((result, index) => {
              const rowKey = rowKeys[index];
              const decision = decisions[rowKey];
              const hint = acceptHint(result);
              return (
                <Stack
                  key={rowKey}
                  gap="xs"
                  p="sm"
                  style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}
                >
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" fw={600}>
                      {result.code}
                    </Text>
                    <Badge size="sm" variant="light" color={severityColor(result.severity)}>
                      {result.severity}
                    </Badge>
                    <Badge size="sm" variant="outline">
                      {actionLabel(result.action)}
                    </Badge>
                  </Group>
                  <Text size="sm">{result.message}</Text>
                  {suggestionText(result) ? (
                    <Text size="xs" c="gray.6">
                      {suggestionText(result)}
                    </Text>
                  ) : null}
                  {hint ? (
                    <Text size="xs" c="dimmed">
                      {hint}
                    </Text>
                  ) : null}
                  <Group gap="xs" mt={4}>
                    <Button
                      size="xs"
                      variant={decision === 'accept' ? 'filled' : 'light'}
                      color="green"
                      disabled={loading}
                      onClick={() => handleAccept(rowKey, result)}
                    >
                      Accept
                    </Button>
                    <Button
                      size="xs"
                      variant={decision === 'reject' ? 'filled' : 'light'}
                      color="red"
                      disabled={loading}
                      onClick={() => handleReject(rowKey)}
                    >
                      Reject
                    </Button>
                  </Group>
                </Stack>
              );
            })}
          </Stack>
        ) : null}

        {!checksLoading &&
        draftChecks.length === 0 &&
        automaticChecks.length === 0 &&
        scrubberResults.length === 0 &&
        metadataErrors.length === 0 &&
        !blocked ? (
          <Alert color="green" variant="light" title="Ready to sign">
            <Text size="sm">All checks passed. No CPT changes suggested.</Text>
          </Alert>
        ) : null}

        <Group justify="space-between" align="center">
          {!allCptDecided && scrubberResults.length > 0 ? (
            <Text size="xs" c="dimmed">
              {rowKeys.filter((key) => decisions[key] == null).length} recommendation
              {rowKeys.filter((key) => decisions[key] == null).length === 1 ? '' : 's'} remaining
            </Text>
          ) : (
            <span />
          )}
          <Group gap="sm">
            <Button variant="default" onClick={onCancel} disabled={loading || checksLoading}>
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleContinue();
              }}
              disabled={!canContinue}
              loading={loading}
            >
              {continueLabel}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
